import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["receptorType", "invoiceDescription", "ivaCondition", "ivaConditionGroup", "receptorTypeHint"]
  static values = {
    companyIvaCondition: String
  }

  connect() {
    this.updateButtonState()
    this.updateInvoiceInfo()
    this.syncIvaCondition()
    this.setupConfirmationHandler()
    this.setupClientConfirmationListeners()
  }

  setupConfirmationHandler() {
    // Interceptar el submit del formulario para mostrar confirmación
    const form = this.element.closest('form')
    if (form) {
      const self = this
      form.addEventListener('submit', function(e) {
        // Si el botón tiene el atributo data-confirmed, permitir el submit
        const submitButton = form.querySelector('input[type="submit"]')
        if (submitButton && !submitButton.dataset.confirmed) {
          e.preventDefault()
          e.stopPropagation()
          self.showConfirmation(e)
        }
      })
    }
  }

  setupClientConfirmationListeners() {
    // Escuchar eventos del cuit-selector controller
    const form = this.element.closest('form')
    if (!form) return

    const cuitSelectorEl = form.querySelector('[data-controller*="cuit-selector"]')
    if (!cuitSelectorEl) return

    cuitSelectorEl.addEventListener('cuit-selector:confirmed', (event) => {
      // Sync the IVA condition from the confirmation panel to the form dropdown
      const ivaConditionId = event.detail?.ivaConditionId
      if (ivaConditionId && this.hasIvaConditionTarget) {
        this.ivaConditionTarget.value = ivaConditionId
      }

      // Auto-set receptor type from confirmed IVA condition
      const ivaToReceptorMap = {
        '1': 'responsable_inscripto',
        '2': 'exento_iva',
        '3': 'consumidor_final',
        '4': 'monotributista'
      }
      const receptorType = ivaToReceptorMap[String(ivaConditionId)]
      if (receptorType && this.hasReceptorTypeTarget) {
        this.receptorTypeTarget.value = receptorType
        if (this.hasReceptorTypeHintTarget) {
          this.receptorTypeHintTarget.innerHTML =
            '<i class="fas fa-check-circle text-success"></i> Tipo de receptor configurado automáticamente desde Colppy'
        }
        this.receptorTypeChanged()
      }

      this.updateButtonState()
    })

    cuitSelectorEl.addEventListener('cuit-selector:rejected', () => {
      this.updateButtonState()
    })

    cuitSelectorEl.addEventListener('cuit-selector:pending', () => {
      this.updateButtonState()
    })
  }

  showConfirmation(event) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }
    
    // Obtener información del formulario
    const form = this.element.closest('form')
    if (!form) return
    
    const receptorType = this.hasReceptorTypeTarget ? this.receptorTypeTarget.value : null
    const invoiceDescription = this.hasInvoiceDescriptionTarget ? this.invoiceDescriptionTarget.value : null
    const netAmountElement = form.querySelector('[data-invoice-form-target="netAmount"]')
    const netAmount = netAmountElement ? netAmountElement.textContent.trim() : '0.00'
    
    // Obtener CUIT seleccionado
    const cuitSelect = form.querySelector('[name="invoice[billing_cuit]"]')
    const selectedCuit = cuitSelect ? cuitSelect.value : null
    const selectedOption = cuitSelect ? cuitSelect.options[cuitSelect.selectedIndex] : null
    let clientName = null
    if (selectedOption && selectedOption.textContent) {
      const parts = selectedOption.textContent.split(' - ')
      clientName = parts.length > 1 ? parts.slice(1).join(' - ') : parts[0]
    }
    
    // Obtener tipo de factura
    const companyIvaCondition = this.companyIvaConditionValue || "responsable_inscripto"
    const invoiceDetails = this.determineInvoiceDetails(companyIvaCondition, receptorType)
    const invoiceType = invoiceDetails ? invoiceDetails.invoiceType : 'N/A'
    
    // Mapear receptor_type a texto legible
    const receptorTypeText = {
      'responsable_inscripto': 'Responsable Inscripto',
      'monotributista': 'Monotributista',
      'consumidor_final': 'Consumidor Final',
      'exento_iva': 'Exento de IVA'
    }[receptorType] || receptorType || 'No seleccionado'

    // Obtener condición IVA seleccionada
    const ivaConditionNames = { '1': 'Responsable Inscripto', '2': 'Exento', '3': 'Consumidor Final', '4': 'Monotributista' }
    const ivaConditionId = this.hasIvaConditionTarget ? this.ivaConditionTarget.value : null
    const ivaConditionText = ivaConditionId ? ivaConditionNames[ivaConditionId] : 'No seleccionada'

    // Crear mensaje de confirmación
    let message = '¿Confirmar creación de factura?\n\n'

    if (clientName && selectedCuit) {
      message += `Cliente: ${clientName}\nCUIT: ${selectedCuit}\n\n`
    } else if (selectedCuit) {
      message += `CUIT: ${selectedCuit}\n\n`
    }

    message += `Tipo de Receptor: ${receptorTypeText}\n`
    message += `Condición IVA (Colppy): ${ivaConditionText}\n`
    message += `Tipo de Factura: Factura ${invoiceType}\n`
    message += `Monto Total: $${netAmount}\n`
    
    if (invoiceDescription) {
      const desc = invoiceDescription.length > 80 ? invoiceDescription.substring(0, 80) + '...' : invoiceDescription
      message += `\nDescripción: ${desc}`
    }

    // Mostrar confirmación
    if (confirm(message)) {
      // Marcar como confirmado y enviar formulario
      const submitButton = form.querySelector('input[type="submit"]')
      if (submitButton) {
        submitButton.dataset.confirmed = 'true'
        submitButton.disabled = true
      }
      // Mostrar overlay de carga antes de enviar
      this.showLoadingOverlay()
      // Enviar formulario directamente
      form.submit()
    }
  }

  showLoadingOverlay() {
    const messages = [
      "Preparando los ingredientes de tu factura...",
      "Horneando la factura en el sistema...",
      "Agregando el toque final de AFIP...",
      "Casi listo, revisando la receta...",
      "Emplatando los datos fiscales...",
      "Sazonando los n\u00fameros con IVA...",
      "El chef est\u00e1 firmando la factura..."
    ]

    const overlay = document.createElement('div')
    overlay.className = 'invoice-loading-overlay'
    overlay.innerHTML = `
      <div class="invoice-loading-content">
        <div class="invoice-loading-receipt">
          <div class="receipt-printer">
            <div class="printer-body">
              <div class="printer-slot"></div>
            </div>
            <div class="receipt-paper">
              <div class="receipt-line"></div>
              <div class="receipt-line short"></div>
              <div class="receipt-line"></div>
              <div class="receipt-line medium"></div>
              <div class="receipt-line short"></div>
            </div>
          </div>
        </div>
        <p class="invoice-loading-message">${messages[0]}</p>
        <div class="invoice-loading-dots">
          <span></span><span></span><span></span>
        </div>
      </div>
    `
    document.body.appendChild(overlay)

    // Rotar mensajes cada 2 segundos
    let index = 0
    this.messageInterval = setInterval(() => {
      index = (index + 1) % messages.length
      const msgEl = overlay.querySelector('.invoice-loading-message')
      if (msgEl) {
        msgEl.style.opacity = '0'
        setTimeout(() => {
          msgEl.textContent = messages[index]
          msgEl.style.opacity = '1'
        }, 300)
      }
    }, 2500)
  }

  receptorTypeChanged() {
    this.updateButtonState()
    this.updateInvoiceInfo()
    this.syncIvaCondition()
  }

  syncIvaCondition() {
    if (!this.hasIvaConditionTarget || !this.hasIvaConditionGroupTarget) return

    const receptorType = this.hasReceptorTypeTarget ? this.receptorTypeTarget.value : null
    if (!receptorType) {
      this.ivaConditionGroupTarget.style.display = 'none'
      return
    }

    // Map receptor type to default IVA condition ID
    // For Factura B (consumidor_final and exento_iva), default to Consumidor Final (3)
    const defaultIvaMap = {
      'responsable_inscripto': '1',
      'monotributista': '4',
      'consumidor_final': '3',
      'exento_iva': '3'  // Default to Consumidor Final for Factura B
    }

    const defaultIva = defaultIvaMap[receptorType] || '3'
    this.ivaConditionTarget.value = defaultIva
    // Keep IVA condition group hidden — value is set programmatically
  }

  updateButtonState() {
    const submitButton = this.element.querySelector('input[type="submit"]')
    if (!submitButton) return

    const selectedReceptorType = this.hasReceptorTypeTarget ? this.receptorTypeTarget.value : null
    const invoiceDescription = this.hasInvoiceDescriptionTarget ? this.invoiceDescriptionTarget.value.trim() : ''

    // Si no hay selección de tipo de receptor, deshabilitar el botón
    if (!selectedReceptorType) {
      submitButton.disabled = true
      return
    }

    // Verificar que la descripción esté completa
    if (!invoiceDescription) {
      submitButton.disabled = true
      return
    }

    // Verificar si se puede determinar el tipo de factura
    const companyIvaCondition = this.companyIvaConditionValue || "responsable_inscripto"
    const invoiceDetails = this.determineInvoiceDetails(companyIvaCondition, selectedReceptorType)

    if (!invoiceDetails) {
      // No se puede determinar el tipo de factura, deshabilitar el botón
      submitButton.disabled = true
      return
    }

    // Verificar que el cliente Colppy esté confirmado
    if (!this.isClientConfirmed()) {
      submitButton.disabled = true
      return
    }

    // Habilitar el botón
    submitButton.disabled = false
  }

  isClientConfirmed() {
    const form = this.element.closest('form')
    if (!form) return true

    const cuitSelectorEl = form.querySelector('[data-controller*="cuit-selector"]')
    if (!cuitSelectorEl) return true

    // Obtener el controller de Stimulus
    const cuitSelectorController = this.application.getControllerForElementAndIdentifier(cuitSelectorEl, 'cuit-selector')
    if (!cuitSelectorController) return true

    return cuitSelectorController.isConfirmed()
  }

  updateInvoiceInfo() {
    const selectedReceptorType = this.hasReceptorTypeTarget ? this.receptorTypeTarget.value : null
    const companyIvaCondition = this.companyIvaConditionValue || "responsable_inscripto"

    if (!selectedReceptorType) {
      this.hideInvoiceInfo()
      return
    }

    const invoiceDetails = this.determineInvoiceDetails(companyIvaCondition, selectedReceptorType)
    if (invoiceDetails) {
      this.showInvoiceInfo(invoiceDetails)
    } else {
      this.hideInvoiceInfo()
    }
  }

  determineInvoiceDetails(companyIvaCondition, receptorType) {
    // Lógica para determinar tipo de factura según empresa y receptor
    if (companyIvaCondition === 'monotributista' || companyIvaCondition === 'responsable_exento') {
      // Monotributista y Responsable Exento siempre emiten Factura C
      return {
        invoiceType: 'C',
        discriminatesVat: false,
        specialLegend: false
      }
    } else if (companyIvaCondition === 'responsable_inscripto') {
      // Responsable Inscripto puede emitir A o B
      if (receptorType === 'responsable_inscripto') {
        return {
          invoiceType: 'A',
          discriminatesVat: true,
          specialLegend: false
        }
      } else if (receptorType === 'monotributista') {
        return {
          invoiceType: 'A',
          discriminatesVat: true,
          specialLegend: true
        }
      } else if (receptorType === 'consumidor_final' || receptorType === 'exento_iva') {
        return {
          invoiceType: 'B',
          discriminatesVat: false,
          specialLegend: false
        }
      } else {
        // Receptor no reconocido
        return null
      }
    } else {
      // Tipo de empresa no reconocido
      return null
    }
  }

  showInvoiceInfo(details) {
    if (!details) return

    const infoDiv = document.getElementById('invoice-info')
    const detailsSpan = document.getElementById('invoice-details')

    if (!infoDiv || !detailsSpan) return

    let message = `Se generará <strong>Factura ${details.invoiceType}</strong>`
    let requirements = []

    if (details.discriminatesVat) {
      requirements.push('Discrimina IVA')
    } else {
      requirements.push('No discrimina IVA')
    }

    if (details.specialLegend) {
      requirements.push('Con leyenda especial para Monotributista')
    }

    if (requirements.length > 0) {
      message += '<ul class="mb-0 mt-2">'
      requirements.forEach(req => {
        message += `<li>${req}</li>`
      })
      message += '</ul>'
    }

    detailsSpan.innerHTML = message
    infoDiv.style.display = 'block'
  }

  hideInvoiceInfo() {
    const infoDiv = document.getElementById('invoice-info')
    if (infoDiv) {
      infoDiv.style.display = 'none'
    }
  }
}
